import { Scene } from './Scene';
import { GltfParser, ResolvedPrimitive } from '../gltf';
import { GltfMesh } from '../mesh';
import { RenderObject } from '../object/RenderObject';
import { TerrainMaterial } from '../material';
import { VolumeHeightSample } from '../navigation-mesh';
import { HeightField, HeightmapHeightField } from './heightfield';
import { TileMeshClient } from './terrain/TileMeshClient';
import { TileMeshOutput } from './terrain/TileMeshGenerator';
import { buildTerrainMaterial } from './TerrainLoader';

type HeightmapGrid = { data: Uint8Array; width: number; height: number };

type LoadedTile = {
  obj: RenderObject;
  mesh: GltfMesh;
  lod: number;
  /**
   * Previous-LOD tile fading out during a crossfade. The fade-in tile lives
   * in `obj`/`mesh`; `prev` is destroyed when `tick()` sees the transition
   * complete.
   */
  prev?: { obj: RenderObject; mesh: GltfMesh; lod: number };
  /**
   * Distance-anchored crossfade state. `startDist` is the chebyshev world
   * distance from camera to tile center at the moment the transition began;
   * `sign` is +1 when the new LOD is coarser (camera moving away) or −1 when
   * finer (camera moving closer). Fade progresses with `sign * (d - startDist)`.
   */
  transition?: { startDist: number; sign: 1 | -1 };
};

/**
 * Distance the camera must travel (chebyshev world units) past the LOD-band
 * boundary before the crossfade is fully complete.
 */
const FADE_DISTANCE_METERS = 100;

/**
 * Concentric LOD bands keyed by chebyshev distance from the camera tile.
 *
 *  - LOD0 covers a 9×9 inner ring
 *  - LOD1 extends to a 13×13 ring
 *  - LOD2 extends to a 17×17 ring
 *  - LOD3 extends to a 21×21 ring (≈ 441 tiles total at the largest band)
 */
const LOD_BANDS = [
  { maxDistance: 4, lodLevel: 0 },
  { maxDistance: 6, lodLevel: 1 },
  { maxDistance: 8, lodLevel: 2 },
  { maxDistance: 10, lodLevel: 3 },
] as const;

/**
 * Outermost chebyshev distance any terrain LOD reaches. Beyond this radius the
 * terrain is culled — anything else camera-tracked (scatter, decals, particles)
 * should cap its own ring at the same value to avoid floating-content artifacts
 * over a culled void.
 */
export const TERRAIN_MAX_RING_DISTANCE = LOD_BANDS[LOD_BANDS.length - 1].maxDistance;
const MAX_RING_DISTANCE = TERRAIN_MAX_RING_DISTANCE;

const tileKey = (tx: number, tz: number): string => `${tx},${tz}`;

/**
 * Camera-driven tiled terrain streamer with concentric LOD rings.
 *
 * Tile meshes are generated client-side by a {@link TileMeshClient} (Web Worker)
 * — the streamer no longer fetches per-tile or whole-world bundle GLBs. The
 * shared {@link TerrainMaterial} (PBR textures + slot metadata) is still loaded
 * once at scene activation from the manifest's shared GLB.
 *
 * The LOD ring logic, fade transitions, and eviction are unchanged from the
 * fetch-based predecessor.
 */
export class TiledTerrainStreamer {
  private loaded = new Map<string, LoadedTile>();

  private tilesX: number;
  private tilesZ: number;
  private tileWorldSize: number;
  private worldMinX: number;
  private worldMinZ: number;
  private worldWidth: number;
  private worldDepth: number;
  private maxLodLevel: number;

  private lastCameraTileX = -999;
  private lastCameraTileZ = -999;
  private lastCameraX = 0;
  private lastCameraZ = 0;

  private sharedMaterial: TerrainMaterial | undefined;

  private heightField: HeightField | null = null;

  constructor(
    private scene: Scene,
    private meshClient: TileMeshClient,
    tileWorldSize: number,
    worldMinX: number,
    worldMinZ: number,
    worldWidth: number,
    worldDepth: number,
    tilesX: number,
    tilesZ: number,
    maxLodLevel: number,
    private sharedMaterialPromise: Promise<TerrainMaterial | undefined>,
    heightmap: HeightmapGrid | null = null,
    heightmapAmplitude: number = 0,
    heightmapCellSize: number = 0,
  ) {
    this.tileWorldSize = tileWorldSize;
    this.worldMinX = worldMinX;
    this.worldMinZ = worldMinZ;
    this.worldWidth = worldWidth;
    this.worldDepth = worldDepth;
    this.tilesX = tilesX;
    this.tilesZ = tilesZ;
    this.maxLodLevel = maxLodLevel;
    if (heightmap) {
      this.heightField = new HeightmapHeightField({
        heightmap,
        amplitude: heightmapAmplitude,
        worldMinX,
        worldMinZ,
        worldWidth,
        worldDepth,
        cellSize: heightmapCellSize,
        tileWorldSize,
        tilesX,
        tilesZ,
      });
    }
  }

  /**
   * Returns the streamer's heightfield (heightmap-driven). Null when the scene
   * has no authored heightmap (noise-only terrain). The asset-manager's
   * `TerrainHeightOracle` consumes this for editor-time height queries; runtime
   * physics in playful uses {@link sampleHeight} directly.
   */
  getHeightField(): HeightField | null {
    return this.heightField;
  }

  /**
   * Sample terrain height at (x, z). When `heightmapCellSize > 0`, snaps to the
   * terrain vertex grid and bilinearly interpolates between the four surrounding
   * vertex heights — matching the piecewise-linear surface the GPU renders exactly.
   * Returns null when no heightmap is available (noise terrain).
   */
  sampleHeight(x: number, z: number): number | null {
    return this.heightField ? this.heightField.sampleHeight(x, z) : null;
  }

  /** Sample height + analytic surface normal via central differences. */
  sampleHeightWithNormal(x: number, z: number): VolumeHeightSample | null {
    if (!this.heightField) return null;
    const sample = this.heightField.sampleHeightWithNormal(x, z);
    if (!sample) return null;
    return { height: sample.height, normal: sample.normal };
  }

  /** Block on the inner 3×3 (LOD0) around (x, z) so the player never spawns into empty terrain. */
  async loadInitialTiles(x: number, z: number): Promise<void> {
    this.sharedMaterial = await this.sharedMaterialPromise;

    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;
    this.lastCameraX = x;
    this.lastCameraZ = z;

    const blockingPromises: Promise<void>[] = [];
    const backgroundLaunches: (() => void)[] = [];
    for (let dx = -MAX_RING_DISTANCE; dx <= MAX_RING_DISTANCE; dx++) {
      for (let dz = -MAX_RING_DISTANCE; dz <= MAX_RING_DISTANCE; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (!this.inRange(ntx, ntz)) continue;
        const cheb = Math.max(Math.abs(dx), Math.abs(dz));
        const lod = this.lodForDistance(cheb);
        if (lod < 0) continue;
        if (cheb <= 1) {
          blockingPromises.push(this.loadTile(ntx, ntz, lod, false));
        } else {
          backgroundLaunches.push(() => void this.loadTile(ntx, ntz, lod, false));
        }
      }
    }
    await Promise.all(blockingPromises);
    for (const launch of backgroundLaunches) launch();
  }

  /** Per-frame hook: re-evaluate concentric LOD rings. No-op when the camera tile hasn't changed. */
  updateCamera(x: number, z: number): void {
    this.lastCameraX = x;
    this.lastCameraZ = z;
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);

    if (tx === this.lastCameraTileX && tz === this.lastCameraTileZ) return;
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    const needed = new Map<string, number>();
    for (let dx = -MAX_RING_DISTANCE; dx <= MAX_RING_DISTANCE; dx++) {
      for (let dz = -MAX_RING_DISTANCE; dz <= MAX_RING_DISTANCE; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (!this.inRange(ntx, ntz)) continue;
        const cheb = Math.max(Math.abs(dx), Math.abs(dz));
        const lod = this.lodForDistance(cheb);
        if (lod < 0) continue;
        needed.set(tileKey(ntx, ntz), lod);
      }
    }

    for (const [key, entry] of [...this.loaded.entries()]) {
      const want = needed.get(key);
      if (want === undefined) {
        this.evictTile(key);
      } else if (want !== entry.lod) {
        // Old LOD stays loaded; loadTile will swap it.
      }
    }

    for (const [key, lod] of needed) {
      const have = this.loaded.get(key);
      if (have && have.lod === lod) continue;
      const [ntxStr, ntzStr] = key.split(',');
      void this.loadTile(Number(ntxStr), Number(ntzStr), lod, true);
    }
  }

  /** Per-frame crossfade driver. */
  public tick(cameraX: number, cameraZ: number): void {
    this.lastCameraX = cameraX;
    this.lastCameraZ = cameraZ;
    for (const [key, entry] of this.loaded) {
      if (!entry.prev || !entry.transition) continue;
      const fade = this.computeFade(key, entry.transition);
      if (fade >= 1) {
        this.scene.objectManager.removeObject(entry.prev.obj);
        entry.prev.mesh.destroy(this.scene.ctx.gl);
        entry.prev = undefined;
        entry.transition = undefined;
        entry.obj.ditherFade = null;
      } else {
        entry.prev.obj.ditherFade = fade;
        entry.obj.ditherFade = -fade;
      }
    }
  }

  private cameraDistanceToTile(tx: number, tz: number): number {
    const cx = this.worldMinX + (tx + 0.5) * this.tileWorldSize;
    const cz = this.worldMinZ + (tz + 0.5) * this.tileWorldSize;
    return Math.max(Math.abs(this.lastCameraX - cx), Math.abs(this.lastCameraZ - cz));
  }

  private computeFade(key: string, transition: { startDist: number; sign: 1 | -1 }): number {
    const [txStr, tzStr] = key.split(',');
    const d = this.cameraDistanceToTile(Number(txStr), Number(tzStr));
    const f = (transition.sign * (d - transition.startDist)) / FADE_DISTANCE_METERS;
    return Math.min(1, Math.max(0, f));
  }

  private inRange(tx: number, tz: number): boolean {
    return tx >= 0 && tx < this.tilesX && tz >= 0 && tz < this.tilesZ;
  }

  private lodForDistance(cheb: number): number {
    for (const band of LOD_BANDS) {
      if (cheb <= band.maxDistance) {
        return Math.min(band.lodLevel, this.maxLodLevel);
      }
    }
    return -1;
  }

  /** Build a `ResolvedPrimitive` from the worker's typed-array output. */
  private buildPrimitive(
    out: TileMeshOutput,
    tx: number,
    tz: number,
    lod: number,
  ): ResolvedPrimitive {
    return {
      attributes: {
        POSITION: { array: out.positions, type: 'VEC3' },
        NORMAL: { array: out.normals, type: 'VEC3' },
        TEXCOORD_0: { array: out.uvs, type: 'VEC2' },
        COLOR_0: { array: out.colors, type: 'VEC4' },
      },
      indices: out.indices,
      extras: {
        _TERRAIN_TILE: true,
        tileX: tx,
        tileZ: tz,
        lodLevel: lod,
      },
    };
  }

  private async loadTile(
    tx: number,
    tz: number,
    lod: number,
    allowTransition: boolean,
  ): Promise<void> {
    const key = tileKey(tx, tz);
    const clampedLod = Math.min(lod, this.maxLodLevel);

    let out: TileMeshOutput;
    try {
      const tileWorldMaxX = Math.min(
        this.worldMinX + (tx + 1) * this.tileWorldSize,
        this.worldMinX + this.worldWidth,
      );
      const tileWorldMaxZ = Math.min(
        this.worldMinZ + (tz + 1) * this.tileWorldSize,
        this.worldMinZ + this.worldDepth,
      );
      out = await this.meshClient.generate(
        {
          tileX: tx,
          tileZ: tz,
          worldMinX: this.worldMinX + tx * this.tileWorldSize,
          worldMinZ: this.worldMinZ + tz * this.tileWorldSize,
          worldMaxX: tileWorldMaxX,
          worldMaxZ: tileWorldMaxZ,
        },
        clampedLod,
      );
    } catch (e) {
      console.warn(`Failed to generate terrain tile (${tx},${tz}) lod${clampedLod}:`, e);
      return;
    }

    // Camera may have moved during the await; drop if this tile no longer wants
    // exactly this LOD.
    if (this.lodForCurrentCamera(tx, tz) !== lod) return;

    const prim = this.buildPrimitive(out, tx, tz, clampedLod);
    const gltfMesh = new GltfMesh(
      `_terrain_${tx}_${tz}_lod${clampedLod}`,
      this.scene.ctx,
      this.scene,
      prim,
    );
    const obj = this.scene.objectFactory.instantiateMesh(gltfMesh);
    if (this.sharedMaterial) obj.material = this.sharedMaterial;
    this.scene.instanceManager.registerObject(obj);

    this.installLoadedTile(key, obj, gltfMesh, clampedLod, allowTransition);
  }

  private installLoadedTile(
    key: string,
    obj: RenderObject,
    gltfMesh: GltfMesh,
    lod: number,
    allowTransition: boolean,
  ): void {
    const existing = this.loaded.get(key);
    const crossfadeOn = this.scene.ctx.engine.graphicsSettings?.lodCrossfadeEnabled ?? false;
    const useTransition = allowTransition && crossfadeOn && !!existing;

    if (!existing) {
      this.loaded.set(key, { obj, mesh: gltfMesh, lod });
      return;
    }

    if (!useTransition) {
      this.scene.objectManager.removeObject(existing.obj);
      existing.mesh.destroy(this.scene.ctx.gl);
      if (existing.prev) {
        this.scene.objectManager.removeObject(existing.prev.obj);
        existing.prev.mesh.destroy(this.scene.ctx.gl);
      }
      this.loaded.set(key, { obj, mesh: gltfMesh, lod });
      return;
    }

    if (existing.prev && existing.prev.lod === lod) {
      // Camera reversed mid-fade and re-requested the same LOD that's still
      // fading out: cancel the transition, restore prev as the active tile.
      this.scene.objectManager.removeObject(existing.obj);
      existing.mesh.destroy(this.scene.ctx.gl);
      existing.prev.obj.castsShadows = true;
      existing.prev.obj.ditherFade = null;
      this.loaded.set(key, {
        obj: existing.prev.obj,
        mesh: existing.prev.mesh,
        lod: existing.prev.lod,
      });
      this.scene.objectManager.removeObject(obj);
      gltfMesh.destroy(this.scene.ctx.gl);
      return;
    }
    if (existing.prev) {
      this.scene.objectManager.removeObject(existing.prev.obj);
      existing.prev.mesh.destroy(this.scene.ctx.gl);
    }
    existing.obj.ditherFade = null;
    existing.obj.castsShadows = false;
    const newPrev = { obj: existing.obj, mesh: existing.mesh, lod: existing.lod };
    const [txStr, tzStr] = key.split(',');
    const startDist = this.cameraDistanceToTile(Number(txStr), Number(tzStr));
    const transition: { startDist: number; sign: 1 | -1 } = {
      startDist,
      sign: lod > existing.lod ? 1 : -1,
    };
    const entry: LoadedTile = { obj, mesh: gltfMesh, lod, prev: newPrev, transition };
    obj.ditherFade = 0;
    newPrev.obj.ditherFade = 0;
    this.loaded.set(key, entry);
  }

  private lodForCurrentCamera(tx: number, tz: number): number {
    const dx = tx - this.lastCameraTileX;
    const dz = tz - this.lastCameraTileZ;
    return this.lodForDistance(Math.max(Math.abs(dx), Math.abs(dz)));
  }

  /**
   * Tear down every loaded tile, the shared material, and the worker.
   * The streamer is unusable afterward; callers must drop their reference.
   */
  destroyAll(): void {
    for (const key of [...this.loaded.keys()]) this.evictTile(key);
    this.meshClient.destroy();
    // TerrainMaterial has no destroy() today; texture array + uniforms ride
    // along with the underlying Material lifecycle. Drop the reference so the
    // GC can reclaim it once tiles are gone.
    this.sharedMaterial = undefined;
  }

  private evictTile(key: string): void {
    const tile = this.loaded.get(key);
    if (!tile) return;
    this.loaded.delete(key);
    this.scene.objectManager.removeObject(tile.obj);
    tile.mesh.destroy(this.scene.ctx.gl);
    if (tile.prev) {
      this.scene.objectManager.removeObject(tile.prev.obj);
      tile.prev.mesh.destroy(this.scene.ctx.gl);
    }
  }
}

/**
 * Parse the shared terrain GLB once and build the {@link TerrainMaterial} that
 * every tile references. Returns `undefined` when the GLB has no
 * `_TERRAIN_SHARED` primitive (e.g. no surface slots configured).
 */
export async function loadTerrainSharedMaterial(
  scene: Scene,
  glbBytes: Uint8Array,
): Promise<TerrainMaterial | undefined> {
  const parser = new GltfParser(glbBytes);
  const { json, binaries } = parser.parse();
  const meshes = parser.resolveAllMeshes(json, binaries);

  for (const mesh of meshes) {
    for (const prim of mesh.primitives) {
      if (!prim.extras?.['_TERRAIN_SHARED']) continue;
      return buildTerrainMaterial(scene.ctx, json, binaries, prim.extras);
    }
  }
  return undefined;
}
